/**
 * [TD-47.6c-E.2] useBidConfidence - live hook driving the slider.
 *
 * Posts the live nomination + user roster snapshot to
 * `/api/analytics/bid-confidence/live` on every (current_bid, player_id)
 * change. Backend computes the formula deterministically (R3.B -
 * proprietary constants stay server-side).
 *
 * Returns: { score, label, maxBid, reasonCode, loading } | null when
 * required inputs are missing OR analytics alpha flag is off.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { authHeaders, useAuth } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useBidConfidence({
  pgfValue,
  currentBid,
  userBudget,
  userRosterFilled,
  rosterSize,
  position,
  rosterFitScore,
  enabled = true,
}) {
  const { user } = useAuth();
  const analyticsEnabled =
    user?.role === "admin" || !!user?.alpha_flags?.pgf_analytics_alpha;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const ready = (
    enabled &&
    analyticsEnabled &&
    pgfValue != null &&
    currentBid != null &&
    userBudget != null &&
    rosterSize != null &&
    typeof rosterFitScore === "number" &&
    typeof position === "string" && position.length > 0
  );

  useEffect(() => {
    if (!ready) {
      setData(null);
      return;
    }
    const myId = ++reqId.current;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.post(
          `${API}/analytics/bid-confidence/live`,
          {
            pgf_value: pgfValue,
            current_bid: currentBid,
            user_budget: userBudget,
            user_roster_filled: userRosterFilled || 0,
            roster_size: rosterSize,
            position,
            roster_fit_score: rosterFitScore,
          },
          { headers: authHeaders() }
        );
        if (cancelled || myId !== reqId.current) return;
        setData({
          score: r.data.score,
          label: r.data.label,
          maxBid: r.data.max_bid,
          reasonCode: r.data.reason_code,
        });
      } catch {
        if (!cancelled && myId === reqId.current) setData(null);
      } finally {
        if (!cancelled && myId === reqId.current) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [
    ready, pgfValue, currentBid, userBudget, userRosterFilled,
    rosterSize, position, rosterFitScore,
  ]);

  if (!data) return null;
  return { ...data, loading };
}

export default useBidConfidence;
